import React, { type JSX } from 'react';
import useGame from '../../contexts/useGame';
import MultiplePointsIndicator from '../MultiplePointsIndicator/MultiplePointsIndicator';
import TalentTreePercentBadge from '../TalentTreePercentBadge/TalentTreePercentBadge';
import {
  type RetailWarcraftTalentTreeBuildNode,
  type RetailWarcraftTalentTreeEntry,
} from '../../typings/viewModels/retailWarcraftTalentTreeBuildNode';
import {
  heatMapPercentageClassName,
  heatMapPercentageForEntry,
} from '../TalentTreeNode/TalentTreeNode';
import { createTalentTreeUrl } from '../../helpers/talentTreeUrl';

export type PassiveTalentTreeNodeProps = {
  node: RetailWarcraftTalentTreeBuildNode;
  entry: RetailWarcraftTalentTreeEntry;
  renderMultiplePointsIndicator?: boolean;
  spellModifierId: number;
};

export default function PassiveTalentTreeNode({
  renderMultiplePointsIndicator = true,
  entry,
  node,
  spellModifierId,
}: PassiveTalentTreeNodeProps): JSX.Element {
  const game = useGame();

  const {
    node: { maxRanks },
    pointsInvested: invested,
    heatMapPercent: percent,
  } = node;
  const { spellId, definitionId, icon, name } = entry;

  const isActive = node.entry?.id === entry.id;
  const isHeatMap = !!percent;

  const relativePct = heatMapPercentageForEntry(node, entry);
  const heatMapClassName = heatMapPercentageClassName(relativePct);

  const Element = spellId ? 'a' : 'span';

  return (
    <Element
      className={`talent-tree-node talent-tree-node-passive ${
        !isActive && !isHeatMap ? 'talent-tree-node--inactive' : ''
      } ${
        isHeatMap
          ? `talent-tree-node--${heatMapClassName} talent-tree-node-passive--${heatMapClassName}`
          : ''
      }`}
      target='_blank'
      rel='noreferrer'
      href={
        spellId
          ? createTalentTreeUrl(
              spellId,
              definitionId,
              invested,
              spellModifierId,
              game.abilityExternalSitePath,
            )
          : ''
      }
      onClick={spellId ? (e) => e.stopPropagation() : undefined}
    >
      <img
        src={
          icon.startsWith('http')
            ? icon
            : `${game.assetServerBaseUrl}/img/warcraft/abilities/${icon}.jpg`
        }
        alt={name}
        loading='lazy'
      />
      {isHeatMap ? <TalentTreePercentBadge percent={relativePct} /> : null}
      {renderMultiplePointsIndicator && maxRanks > 1 && invested >= 1 ? (
        <MultiplePointsIndicator amount={invested} />
      ) : null}
    </Element>
  );
}
